import { getApiKey } from '../config/mapyApi';

interface ReverseGeocodeItem {
    name: string;
    label: string;
    position: {
        lon: number;
        lat: number;
    };
    type: string;
    regionalStructure?: { name: string; type: string }[];
}

interface ReverseGeocodeResponse {
    items: ReverseGeocodeItem[];
}

export interface MunicipalityInfo {
    name: string;
    lat: number;
    lon: number;
}

/**
 * Získá informace o nejbližší obci (název a souřadnice středu) z GPS souřadnic.
 *
 * Používá reverse geocode API, které vrací entity na všech administrativních úrovních.
 * Přímo z odpovědi vytáhne položku typu 'regional.municipality' i s její pozicí,
 * čímž se vyhne problému s duplicitními názvy obcí (dříve se název hledal
 * přes Suggest API, které mohlo vrátit jinou stejnojmennou obec).
 */
export const getNearestMunicipality = async (lat: number, lon: number): Promise<MunicipalityInfo | null> => {
    try {
        const apiKey = getApiKey();
        const response = await fetch(
            `https://api.mapy.com/v1/rgeocode?lon=${lon}&lat=${lat}&apikey=${apiKey}`
        );

        if (!response.ok) {
            console.error('Reverse geocode API error:', response.status);
            return null;
        }

        const data: ReverseGeocodeResponse = await response.json();

        if (!data.items || data.items.length === 0) {
            console.warn('Reverse geocode returned no items for position:', lat, lon);
            return null;
        }

        // Hledáme přímo položku typu 'regional.municipality' v items — ta má správnou pozici
        const municipalityItem = data.items.find(item => item.type === 'regional.municipality');

        if (municipalityItem) {
            return {
                name: municipalityItem.name,
                lat: municipalityItem.position.lat,
                lon: municipalityItem.position.lon,
            };
        }

        // Fallback: pokud v items není municipality item, zkusíme název z regionalStructure
        const regionalStructure = data.items[0].regionalStructure;
        if (regionalStructure) {
            const municipality = regionalStructure.find(r => r.type === 'regional.municipality');
            if (municipality) {
                // Nemáme přesnou pozici obce, použijeme pozici nejbližší entity jako přiblížení
                console.warn('Municipality item not found in rgeocode items, using approximate position from:', data.items[0].type);
                return {
                    name: municipality.name,
                    lat: data.items[0].position.lat,
                    lon: data.items[0].position.lon,
                };
            }
        }

        console.warn('Could not determine municipality for position:', lat, lon);
        return null;
    } catch (error) {
        console.error('Error in reverse geocoding:', error);
        return null;
    }
};
